/**
 * Constants for media permissions system
 * Includes browser detection patterns, error codes, and instruction sets
 */

// Permission States
export const PERMISSION_STATES = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
  UNKNOWN: 'unknown',
}

// Browser Types
export const BROWSER_TYPES = {
  CHROME: 'chrome',
  SAFARI: 'safari',
  FIREFOX: 'firefox',
  EDGE: 'edge',
  SAMSUNG: 'samsung',
  OPERA: 'opera',
  UNKNOWN: 'unknown',
}

// Operating Systems
export const OS_TYPES = {
  WINDOWS: 'windows',
  MAC: 'mac',
  IOS: 'ios',
  ANDROID: 'android',
  LINUX: 'linux',
  UNKNOWN: 'unknown',
}

// Device Types
export const DEVICE_TYPES = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
  TABLET: 'tablet',
}

// Media Types
export const MEDIA_TYPES = {
  CAMERA: 'camera',
  MICROPHONE: 'microphone',
  BOTH: 'both',
}

// Error Codes from getUserMedia
export const MEDIA_ERROR_CODES = {
  NOT_ALLOWED: 'NotAllowedError',
  NOT_FOUND: 'NotFoundError',
  NOT_READABLE: 'NotReadableError',
  OVERCONSTRAINED: 'OverconstrainedError',
  TYPE_ERROR: 'TypeError',
  ABORT: 'AbortError',
  SECURITY: 'SecurityError',
}

// Permission Request Results
export const REQUEST_RESULTS = {
  SUCCESS: 'success',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  ERROR: 'error',
  DEVICE_NOT_FOUND: 'device_not_found',
  ALREADY_IN_USE: 'already_in_use',
}

// LocalStorage Keys
export const STORAGE_KEYS = {
  PERMISSION_HISTORY: 'aumhum_media_permissions_history',
  EDUCATION_SHOWN: 'aumhum_permission_education_shown',
  LAST_PERMISSION_CHECK: 'aumhum_last_permission_check',
}

// Browser Detection Patterns
export const BROWSER_PATTERNS = {
  [BROWSER_TYPES.EDGE]: /Edg/i,
  [BROWSER_TYPES.CHROME]: /Chrome/i,
  [BROWSER_TYPES.SAFARI]: /^((?!chrome|android).)*safari/i,
  [BROWSER_TYPES.FIREFOX]: /Firefox/i,
  [BROWSER_TYPES.SAMSUNG]: /SamsungBrowser/i,
  [BROWSER_TYPES.OPERA]: /OPR|Opera/i,
}

// OS Detection Patterns
export const OS_PATTERNS = {
  [OS_TYPES.WINDOWS]: /Windows/i,
  [OS_TYPES.MAC]: /Macintosh|Mac OS X/i,
  [OS_TYPES.IOS]: /iPhone|iPad|iPod/i,
  [OS_TYPES.ANDROID]: /Android/i,
  [OS_TYPES.LINUX]: /Linux/i,
}

// Browser-specific instruction steps
export const BROWSER_INSTRUCTIONS = {
  [BROWSER_TYPES.CHROME]: {
    desktop: [
      'Click the lock icon (🔒) or camera icon in the address bar',
      'Click on "Site settings"',
      'Find Camera and Microphone in the permissions list',
      'Change both to "Allow"',
      'Refresh this page',
    ],
    mobile: [
      'Tap the three dots menu (⋮) in the top right',
      'Tap "Settings"',
      'Tap "Site settings"',
      'Tap "Camera" and then "Microphone"',
      'Select "Allow"',
      'Return to this page and refresh',
    ],
  },
  [BROWSER_TYPES.SAFARI]: {
    desktop: [
      'Click "Safari" in the menu bar',
      'Select "Settings for This Website"',
      'Find Camera and Microphone',
      'Change both to "Allow"',
      'Refresh this page',
    ],
    mobile: [
      'Open iOS Settings app',
      'Scroll down and tap "Safari"',
      'Tap "Camera" and select "Allow"',
      'Tap back, then tap "Microphone" and select "Allow"',
      'Return to Safari and refresh this page',
    ],
    pwa: [
      'Open iOS Settings app',
      'Scroll down to find "AUMHUM" app',
      'Tap on the AUMHUM app',
      'Enable Camera and Microphone toggles',
      'Return to the app',
    ],
  },
  [BROWSER_TYPES.FIREFOX]: {
    desktop: [
      'Click the camera/microphone icon in the address bar',
      'Click the "X" next to blocked permissions',
      'Click "Allow" when prompted',
      'Refresh this page',
    ],
    mobile: [
      'Tap the lock icon in the address bar',
      'Tap "Edit Site Settings"',
      'Find Camera and Microphone',
      'Change both to "Allow"',
      'Return to this page and refresh',
    ],
  },
  [BROWSER_TYPES.EDGE]: {
    desktop: [
      'Click the lock icon (🔒) in the address bar',
      'Click on "Permissions for this site"',
      'Find Camera and Microphone',
      'Change both to "Allow"',
      'Refresh this page',
    ],
    mobile: [
      'Tap the three dots menu (⋯) at the bottom',
      'Tap "Settings"',
      'Tap "Site permissions"',
      'Tap "Camera" and then "Microphone"',
      'Select "Allow"',
      'Return to this page and refresh',
    ],
  },
}

// Educational messages for permission requests
export const EDUCATION_MESSAGES = {
  VIDEO_CALL: {
    title: 'Join with Video and Audio',
    subtitle: 'To participate in the video call, we need access to your camera and microphone',
    benefits: [
      'Connect face-to-face with your practitioner',
      'Have meaningful conversations',
      'Get the most from your session',
    ],
    reassurance: 'You can turn off your camera or microphone at any time during the call.',
  },
  MICROPHONE_ONLY: {
    title: 'Join with Audio',
    subtitle: 'To participate in the audio call, we need access to your microphone',
    benefits: [
      'Speak with your practitioner',
      'Have a private conversation',
      'Get the support you need',
    ],
    reassurance: 'You can mute yourself at any time during the call.',
  },
  CAMERA_ONLY: {
    title: 'Enable Your Camera',
    subtitle: 'To show your video, we need access to your camera',
    benefits: [
      'Let others see you',
      'Enhance the connection',
      'Participate more naturally',
    ],
    reassurance: 'You can turn off your camera at any time.',
  },
}

// Permission blocked messages
export const BLOCKED_MESSAGES = {
  BOTH: {
    title: 'Camera and Microphone Access Blocked',
    description: 'It looks like camera and microphone access has been blocked for this site. To join the video call, you\'ll need to allow access.',
  },
  CAMERA: {
    title: 'Camera Access Blocked',
    description: 'It looks like camera access has been blocked for this site. To show your video, you\'ll need to allow camera access.',
  },
  MICROPHONE: {
    title: 'Microphone Access Blocked',
    description: 'It looks like microphone access has been blocked for this site. To speak in the call, you\'ll need to allow microphone access.',
  },
}

// PWA detection
export function isPWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  )
}

// Check if running on secure context
export function isSecureContext() {
  return window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost'
}

// Asset paths for instruction visuals
export const getInstructionAssetPath = (browser, device, filename) => {
  return `/assets/permission-help/${browser}/${device}-${filename}`
}

